import { Component, OnInit, ViewEncapsulation, Input, ViewChild, SimpleChanges, EventEmitter, Output, ChangeDetectorRef, OnChanges } from '@angular/core';
import { ApiService, ABCisApi } from 'src/app/handlers/apiHandler.service';
import { LazyLoadEvent } from 'primeng/api';
import { SelectItem } from 'primeng/api';
import { FilterMetadata, SortMeta } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';
import { Table } from 'primeng/table/table';
import { first, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { FormGroup, FormControl, ValidatorFn, FormArray } from '@angular/forms';
import { LoggerService } from 'src/app/handlers/logger.service';
import * as moment from 'moment';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'abcis-table',
  standalone: false,
  templateUrl: './abcis-table.component.html',
  styleUrls: ['./abcis-table.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AbcisTableComponent implements OnInit, OnChanges {
  // Component Variables
  @Input() configuration: ABCisTableModel;
  @ViewChild('dt', { static: true }) private dataTable: Table;
  @Output() onSelectRow = new EventEmitter<any>();
  @Output() onUnSelectRow = new EventEmitter<any>();
  @Output() linkClick: EventEmitter<any> = new EventEmitter();
  @Output() customAdd: EventEmitter<any> = new EventEmitter();
  @Output() actionClick: EventEmitter<any> = new EventEmitter();

  formUrl = '';
  hasParameter = false;

  currentLazyLoad: string;

  data: any[];
  cols: any[];
  defaultConfiguration: any = {};
  selectFields: string[] = [];
  isMultipleSelectionMode = true;

  searchForm: FormGroup = new FormGroup({});
  editForm: FormGroup = new FormGroup({});
  listEditForm = new FormGroup({
    editForms: new FormArray([])
  });

  get editForms() {
    return this.listEditForm.controls['editForms'] as FormArray;
  }

  maxRowNumber = new FormControl(50);
  yearRange: string = (new Date().getFullYear() - 20) + ':' + (new Date().getFullYear());
  rowNumberList: SelectItem[] = [
    { label: '1', value: 1 },
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 }
  ];
  loading: boolean;
  totalRecords: number;

  booleanDataSet: Dataset[] = [{ label: '', value: undefined }, { label: 'Yes', value: true }, { label: 'No', value: false }];
  matchModeDataSet: Dataset[] = [{ label: 'Equals', value: 'e' }, { label: 'Contains', value: 'c' }, { label: 'StartsWith', value: 's' }];

  editingObj: EditObject = new EditObject();
  selectedRows = [];
  isEditingAll = new FormControl('');
  editCount = 0;
  primaryFields: any[];
  addingForm: FormGroup;
  lookupCount: number;
  addFormHeader: string;

  currentEvent: LazyLoadEvent;

  constructor(private apiHandler: ApiService,
              private _route: ActivatedRoute,
              private _router: Router,
              private toastr: ToastrService,
              private logger: LoggerService,
              private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.InitializeTableConfiguration();
    this.InitializeAddFormConfiguration();
    this.ConfigureDropDownLookups();
  }

  ngOnChanges(simpleChange: SimpleChanges) {
    if (JSON.stringify((<any>simpleChange).configuration.previousValue) != JSON.stringify((<any>simpleChange).configuration.currentValue)) {
      this.InitializeTableConfiguration();
      this.InitializeFieldProperties();
      this.InitializeStaticProperties();
      this.InitializeParamUrl();
      this.InitializeParamFilters();
      this.InitializeTablDefaultParamsFilters();
      this.ConfigureDropDownLookups();
      this.RefreshTable();
    }
  }

  ConfigureDropDownLookups() {
    this.loading = true;

    this.lookupCount = 0;
    this.cols.forEach((element: ColumnData) => {
      if (element.setUrl != undefined && (element.type == 'dropdown')) {
        this.apiHandler.getABCisRequest(ABCisApi.Support, element.setUrl).then((response) => {
          this.lookupCount--;
          element.dataset = <Dataset[]>response.body;
          this.configureInsertFields(element);
          if (this.lookupCount == 0) this.loading = false;
        }).catch((response: HttpErrorResponse) => {
          this.lookupCount--;
          if (this.lookupCount == 0) this.loading = false;
        });
        this.lookupCount++;
      }
    });
  }

  ConfigureMultiSelectLookups() {
    this.cols.forEach((element: ColumnData) => {
      if (element.type == ColumnType.MultiSelectComplex) {
        // Extract unique values from table data for display purposes
        const uniqueValues = new Set();
        const newData: Dataset[] = [];

        for (let i = 0; i < this.data.length; i++) {
          const value = this.data[i][element.field];
          if (value != null && value != undefined && !uniqueValues.has(value)) {
            uniqueValues.add(value);
            newData.push(<Dataset>{
              value: value,
              label: `${value}` // Start with value as label
            });
          }
        }

        if (!element.columnset) element.columnset = [];

        newData.forEach(newItem => {
          const existingIndex = element.columnset.findIndex(existing => existing.value === newItem.value);
          if (existingIndex === -1) {
            element.columnset.push(newItem);
          }
        });

        // For single select with backend search disabled, set dataset immediately
        if (element.isSingleSelect && element.isFilterBackendSearch === false) {
          element.dataset = [...element.columnset];
        } else if (element.isFilterBackendSearch === false) {
          element.dataset = [...element.columnset];
        }

        // Load proper labels for all unique values
        if (newData.length > 0 && element.setUrl) {
          // For single select without backend search, load labels immediately
          if (element.isSingleSelect && element.isFilterBackendSearch === false) {
            this.loadLabelsForSingleSelect(element, newData);
          } else {
            this.loadLabelsForDisplay(element, newData);
          }
        }
      }
    });

    // Trigger change detection after updating datasets
    this.cdr.detectChanges();
  }

  private loadLabelsForDisplay(element: ColumnData, columnset: Dataset[]): void {
    // Check if we already have labels for these values
    const valuesNeedingLabels = columnset.filter(item =>
      item.label === `${item.value}` || item.label.startsWith('Loading...')
    );

    if (valuesNeedingLabels.length === 0) {
      return;
    }

    const values = valuesNeedingLabels.map(item => item.value).join(',');
    const params = element.addedComplexFieldsUrlParam?.['filter'] || '';
    const url = `${element.setUrl}?mode=table&values=${values}${params}`;

    this.apiHandler.getABCisRequest<Dataset[]>(ABCisApi.Support, url)
      .then(response => {
        const responseBody = response.body as Dataset[];
        if (responseBody) {
          responseBody.forEach(apiItem => {
            const existingIndex = element.columnset.findIndex(existing => existing.value === apiItem.value);
            if (existingIndex >= 0) {
              element.columnset[existingIndex] = apiItem;
            } else {
              element.columnset.push(apiItem);
            }
          });

          // Only update dataset if using existing data for filtering
          if (element.isFilterBackendSearch === false) {
            element.dataset = [...element.columnset];
          }

          this.cdr.detectChanges();
        }
      })
      .catch(error => {
        console.error('❌ Error loading labels for', element.field, ':', error);
      });
  }

  // For single select that doesn't enable API searching
  private loadLabelsForSingleSelect(element: ColumnData, columnset: Dataset[]): void {
    const values = columnset.map(item => item.value).join(',');
    const params = element.addedComplexFieldsUrlParam?.['filter'] || '';
    const url = `${element.setUrl}?mode=table&values=${values}${params}`;

    this.apiHandler.getABCisRequest<Dataset[]>(ABCisApi.Support, url)
      .then(response => {
        const responseBody = response.body as Dataset[];
        if (responseBody) {
          // Update both dataset and columnset with proper labels
          element.dataset = responseBody;
          element.columnset = responseBody;
          // Trigger change detection after async update
          this.cdr.detectChanges();
        }
      })
      .catch(error => {
        console.error('❌ Error loading single select labels:', error);
      });
  }

  private loadLabelsForMultiSelectComplex(element: ColumnData, columnset: Dataset[]): void {
    const values = columnset.map(item => item.value).join(',');
    const params = element.addedComplexFieldsUrlParam?.['filter'] || '';
    const url = `${element.setUrl}?mode=table&values=${values}${params}`;

    this.apiHandler.getABCisRequest<Dataset[]>(ABCisApi.Support, url)
      .then(response => {
        const responseBody = response.body as Dataset[];
        if (responseBody) {
          // Update both dataset and columnset with proper labels
          element.dataset = responseBody;
          element.columnset = responseBody;
          // Trigger change detection after async update
          this.cdr.detectChanges();
        }
      })
      .catch(error => {
        console.error('❌ Error loading multiselect complex labels:', error);
      });
  }

  ConfigureMultiSelectFieldParams(filterType: string, postFix: string) {
    let formGroup: FormGroup;
    let isFilter = false;
    switch (filterType) {
    case 'addRow':
      formGroup = this.addingForm;
      break;
    case 'editRow':
      formGroup = this.editForm;
      break;
    case 'filter':
      isFilter = true;
      break;
    }
    if (isFilter) {
      this.cols.forEach((element: ColumnData) => {
        this.configuration.apiConfig.urlParams.forEach(urlParam => {
          if (element.addedComplexFieldsUrlParam === undefined) element.addedComplexFieldsUrlParam = {};
          element.addedComplexFieldsUrlParam[filterType] = '';
          if (urlParam.value != '') {
            element.addedComplexFieldsUrlParam[filterType] += '&';
            element.addedComplexFieldsUrlParam[filterType] += `${urlParam.name}=${urlParam.value}`;
          }
        });
      });
    } else {
      this.cols.forEach((element: ColumnData) => {
        if (element.addedComplexFieldsUrlParam === undefined) element.addedComplexFieldsUrlParam = {};
        element.addedComplexFieldsUrlParam[filterType] = '';
        if (element.setUrl != undefined && element.type == 'multiselectcomplex') {
          if (element.addedComplexFields != undefined) {
            let setAdded = '';
            element.addedComplexFields.forEach(col => {
              if (formGroup.controls[col + postFix] !== undefined) {
                setAdded += `&${col}s=${formGroup.controls[col + postFix].value}`;
              }
            });
            element.addedComplexFieldsUrlParam[filterType] = setAdded;
          }
        }
      });
    }
  }

  configureInsertFields(columnData: ColumnData) {
    if (!columnData.notInForm) {
      if (columnData.allowedInsertDataset != undefined) {
        const newData: Dataset[] = [];
        for (let i = 0; i < columnData.allowedInsertDataset.length; i++) {
          newData.push(<Dataset>{
            value: columnData.allowedInsertDataset[i].value,
            label: columnData.dataset.find(x => x.value == columnData.allowedInsertDataset[i].value).label
          });
        }
        columnData.allowedInsertDataset = newData;
      } else {
        columnData.allowedInsertDataset = columnData.dataset;
      }
    }
  }

  InitializeParamUrl() {
    let first = true;
    const table = (this.configuration.isTable) ? '/table' : '';
    this.formUrl = this.configuration.apiConfig.url.toString();
    this.configuration.apiConfig.url += table;
    this.hasParameter = false;
    if (this.configuration.apiConfig.urlParams == undefined) this.configuration.apiConfig.urlParams = [];
    this.configuration.apiConfig.urlParams.forEach(urlParam => {
      if (urlParam.value != '') {
        if (first) {
          this.configuration.apiConfig.url += '?';
          this.hasParameter = true;
          first = false;
        } else {
          this.configuration.apiConfig.url += '&';
        }
        this.configuration.apiConfig.url += `${urlParam.name}=${urlParam.value}`;
        this._router.navigate([], {
          relativeTo: this._route.root,
          queryParams: {
            [urlParam.name]: urlParam.value
          },
          queryParamsHandling: 'merge'
        });
      }
    });
  }

  onRowSelect($event) {
    this.onSelectRow.next($event);
  }

  onRowUnselect($event) {
    this.selectedRows = [];
    this.onUnSelectRow.next($event);
  }

  InitializeFieldProperties() {
    this.primaryFields = [];
    const group = {};
    this.configuration.colConfig.forEach(element => {
      let validators = [];
      if (element.addFormValidators != undefined) {
        validators = element.addFormValidators;
      }
      group[element.field] = new FormControl('', validators);
      this.editingObj.addedRowValue.push({ [element.field]: '' });
      if (element.isPrimary) {
        this.primaryFields.push(element.field);
      }
    });
    this.addingForm = new FormGroup(group);
  }

  /**
   * Sets the url paramter filters. This allows users to share tables.
   */
  InitializeParamFilters() {
    this._route.queryParams.pipe(first()).subscribe(params => {
      // Find Parameters
      const query = params[this.configuration.queryParameterName];
      if (query !== undefined) {
        // Extract the object from the param
        const currentSort: Query = JSON.parse(decodeURIComponent(query));
        this.defaultConfiguration.MultiSortMeta = currentSort.ob;

        // Set the max row number
        this.maxRowNumber.setValue(currentSort.rc);

        // Foreach filter. Find the filter it represent: Then filter and set the field
        for (var key in currentSort.f) {
          for (let i = 0; i < this.cols.length; i++) {
            if (key == this.cols[i].field) {
              let val;
              if (this.cols[i].type == ColumnType.Date) {
                // Make sure the date field is a date
                val = new Date(currentSort.f[key].value);
              } else {
                val = currentSort.f[key].value;
              }

              // Filter and show the user it has been filtered
              this.cols[i].value = val;
              this.searchForm.get(this.cols[i].field + 'SearchField').setValue(val);
              this.cols[i].Matchmode = currentSort.f[key].matchMode;
              setTimeout(() => {
                this.dataTable.filter(val, this.cols[i].field, currentSort.f[key].matchMode);
              }, 250);
            }
          }
        }
      }
    });
  }

  /**
   * Sets the table defaut params filter.
   */
  InitializeTablDefaultParamsFilters(){
    /**
     * Todo: Implement setting of default sorting and page size
     */
    if(this.configuration.apiConfig.queryParameters?.length > 0){
      this.configuration.apiConfig.queryParameters.forEach(e => {
        this.dataTable.filter(e.value, e.name, e.mode);
      });
    }
  }

  /**
   * Initialized the tables configuration variables
   */
  InitializeTableConfiguration() {
    // Set the main column configuration
    this.cols = <any[]>this.configuration.colConfig;
    this.selectFields = [];
    const group = {};
    // Set Query Select field
    this.cols.forEach((element: ColumnData) => {
      if (element.sortable === undefined) {
        element.sortable = true;
      }
      if (element.type === ColumnType.Action) {
        element.sortable = false;
      } else {
        this.selectFields.push(element.field);
      }
      if (element.matchmode == undefined) element.matchmode = MatchMode.Contains;
      if (element.type == ColumnType.MultiSelect) element.matchmode = MatchMode.In;
      if (element.type == ColumnType.MultiSelectComplex) element.matchmode = MatchMode.In;

      // Initialize addedComplexFieldsUrlParam for MultiSelectComplex columns
      if (element.type == ColumnType.MultiSelectComplex) {
        if (element.addedComplexFieldsUrlParam === undefined) {
          element.addedComplexFieldsUrlParam = {};
        }
        // Initialize all the required keys
        element.addedComplexFieldsUrlParam['filter'] = '';
        element.addedComplexFieldsUrlParam['addRow'] = '';
        element.addedComplexFieldsUrlParam['editRow'] = '';
      }

      // Create form control for search
      const searchControl = new FormControl();
      this.searchForm.addControl(element.field + 'SearchField', searchControl);

      if (element.type === ColumnType.MultiSelectComplex && element.isFilterBackendSearch === undefined) {
        element.isFilterBackendSearch = true; // Default to backend search
      }
      // Set up value change listeners for filtering based on column type
      if (element.type === ColumnType.String || element.type === ColumnType.TextArea) {
        searchControl.valueChanges.pipe(
          debounceTime(500),
          distinctUntilChanged()
        ).subscribe(value => {
          this.filterTable(value, element.field, element.matchmode);
        });
      } else if (element.type === ColumnType.Date) {
        searchControl.valueChanges.pipe(
          debounceTime(500),
          distinctUntilChanged()
        ).subscribe(value => {
          if (value) {
            this.filterDate(value, false, element.field);
          } else {
            this.dataTable.filter('', element.field, element.matchmode);
          }
        });
      }

      let validators = [];
      if (element.addFormValidators != undefined) {
        validators = element.addFormValidators;
      }
      group[element.field] = new FormControl('', validators);

      for (let i = 0; i < 100; i++) {
        this.editForm.addControl(element.field + i + 'EditField', new FormControl('', validators));
      }

      if(element.type == ColumnType.String && element.accept === undefined) {
        element.accept = ColumnValues.Alphanumeric;
      }
    });

    // Set the url parameter name
    if (this.configuration.queryParameterName === undefined) {
      this.configuration.queryParameterName = 'tableconfiguration';
    }

    if (this.configuration.isTable === undefined) {
      this.configuration.isTable = true;
    }

    if (this.configuration.isFilterable === undefined) {
      this.configuration.isFilterable = true;
    }

    this.editingObj.isEditing = false;
    if (this.configuration.isConfigurable == undefined) this.configuration.isConfigurable = false;
    if (this.configuration.isConfigurable) {
      if (this.configuration.canAdd == undefined) this.configuration.canAdd = true;
      if (this.configuration.canDelete == undefined) this.configuration.canDelete = true;
      if (this.configuration.canEdit == undefined) this.configuration.canEdit = true;
    }

    if(this.configuration.selectionMode === undefined){
      this.configuration.selectionMode = 'multiple';
    } else {
      this.isMultipleSelectionMode = this.configuration.selectionMode === 'multiple';
    }

    if(this.configuration.metaKeySelection === undefined){
      this.configuration.metaKeySelection = true;
    }

    // Force fixed table layout from the start
    setTimeout(() => {
      this.calculateAndApplyColumnWidths();
    }, 0);
  }

  private calculateAndApplyColumnWidths() {
    if (!this.cols || this.cols.length === 0) return;

    const isInDialog = this.dataTable?.el?.nativeElement?.closest('p-dialog');
    if (isInDialog) {
      return; // Don't apply width calculations for tables in dialogs
    }

    const tableElement = this.dataTable?.el?.nativeElement?.querySelector('table');
    if (!tableElement) return;

    // Force table layout to fixed
    tableElement.style.tableLayout = 'fixed';
    tableElement.style.width = '100%';

    // Calculate available width (accounting for scrollbars and padding)
    const container = this.dataTable.el.nativeElement.querySelector('.p-datatable-wrapper');
    const containerWidth = container ? container.offsetWidth - 20 : 1000; // Fallback width

    let totalFixedWidth = 0;
    let flexColumns = 0;
    const visibleCols = this.cols.filter(col => !col.isHidden);

    // First pass: calculate fixed widths
    visibleCols.forEach((col: ColumnData) => {
      if (col.width) {
        const numericWidth = parseInt(col.width.replace(/\D/g, ''));
        totalFixedWidth += numericWidth;
      } else {
        flexColumns++;
      }
    });

    // Calculate remaining width for flex columns
    const remainingWidth = Math.max(containerWidth - totalFixedWidth, 0);
    const flexColumnWidth = flexColumns > 0 ? Math.floor(remainingWidth / flexColumns) : 0;

    // Apply calculated widths
    visibleCols.forEach((col: ColumnData, index: number) => {
      if (!col.width) {
        col.calculatedWidth = `${Math.max(flexColumnWidth, 100)}px`; // Minimum 100px
      } else {
        col.calculatedWidth = col.width;
      }
    });

    // Force immediate application
    this.cdr.detectChanges();
  }


  /**
   *  Initialized the add form configuration variables
   */
  InitializeAddFormConfiguration() {
    this.addFormHeader = this.configuration.addFormConfig?.header ?? 'Add New Row';
  }

  /**
   * Set the control paramters
   */
  InitializeStaticProperties() {
    // The table is loading initially
    this.loading = true;

    // Find how many approxamite records there are. This allows pagination.
    this.totalRecords = this.configuration.apiConfig.approxamateRowCount;

    // Set the max row number
    if (this.configuration.initialRowCount !== undefined){
      this.maxRowNumber.setValue(this.configuration.initialRowCount);
    }
  }

  RefreshTable() {
    this.dataTable.filter('', '', '');
  }

  filterTable(eventOrValue: Event | string | null, field: string, matchmode: string) {
    const value = typeof eventOrValue === 'string'
      ? eventOrValue
      : (eventOrValue?.target as HTMLInputElement)?.value ?? '';

    setTimeout(() => {
      // this.dataTable.first = 0;
      this.dataTable.filter(value, field, matchmode);
    }, 100);
  }

  onNumberKeyPress(event) {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode != 46 && charCode > 31
      && (charCode < 48 || charCode > 57))
      return false;

    return true;
  }

  loadLazy(event: LazyLoadEvent) {
    // Set the query param.
    const query: Query = {
      sf: this.selectFields, // Select
      f: event.filters, // Where
      ob: event.multiSortMeta, // Order By
      fr: event.first, // Limits
      rc: event.rows
    };

    this.currentEvent = event;

    if (JSON.stringify(this.currentLazyLoad) !== JSON.stringify(query)) { // JSON Stringify...
      this.currentLazyLoad = JSON.stringify({ ...query });
      // Show that table is busy
      this.loading = true;
      // Set the query param to a url parameter string
      const filterQuery = { ...query };
      filterQuery.f = this.FilterToArray(event.filters);
      filterQuery.sf = filterQuery.sf.join(',');
      filterQuery.ob = this.SortToArray(event.multiSortMeta);
      const paramArray = this.QueryToParamArray(filterQuery);
      let queryCriteria = `${(this.hasParameter) ? '&' : '?'}${this.ParamToString(paramArray)}`;

      queryCriteria= this.configuration.apiConfig.anyParams? queryCriteria:'';
      this.logger.debug(paramArray.toString());

      // Send a get request with the query and set the values we get from the database
      this.apiHandler.getABCisRequest<any>(this.configuration.apiConfig.api, this.configuration.apiConfig.url + queryCriteria).then((val) => {
        this.data = val.body;

        // Update totalRecords based on actual results
        if (this.data && Array.isArray(this.data)) {
          // If we got fewer records than requested, we're at the end
          if (this.data.length < event.rows) {
            this.totalRecords = event.first + this.data.length;
          } else if (this.data.length === 0 && event.first > 0) {
            // If no data and we're not on first page, reset to first page
            this.totalRecords = 0;
            this.dataTable.first = 0;
            // Trigger a new load for the first page
            setTimeout(() => {
              this.dataTable._filter();
            }, 100);
            this.loading = false;
            return;
          } else {
            // We have a full page, so there might be more
            this.totalRecords = Math.max(this.totalRecords, event.first + this.data.length + 1);
          }
        }

        this.ConfigureMultiSelectLookups();
        this.loading = false;

        // Force boolean column widths after data loads
        // this.forceBooleanColumnWidths();
        setTimeout(() => {
          this.calculateAndApplyColumnWidths();
          this.forceBooleanColumnWidths();
          // this.forceDateColumnWidths();
        }, 100);

      }).catch((response: HttpErrorResponse) => {
        console.log(response);
        this.SendMessage(true, 'error', response.error, response.statusText);
        this.loading = false;

        // If we get an error and we're not on the first page, reset to first page
        if (event.first > 0) {
          this.dataTable.first = 0;
          this.totalRecords = this.configuration.apiConfig.approxamateRowCount;
          setTimeout(() => {
            this.dataTable._filter();
          }, 100);
        }
      });

      // Route the users filter results. For sharing
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: {
          [this.configuration.queryParameterName]: encodeURIComponent(JSON.stringify(query))
        },
        queryParamsHandling: 'merge'
      });
    }
  }

  ParamToString(paramArray: any[]) {
    let ret = '';
    paramArray.forEach(element => {
      ret += `&${element.param}=${element.value}`;
    });
    return ret.substr(1, ret.length - 1);
  }

  QueryToParamArray(query: Query): any[] {
    const s: any[] = [];
    Object.keys(query).forEach((element) => {
      let iter = 0;
      if (query[element] instanceof Array) {
        query[element].forEach(elementtwo => {
          if (elementtwo instanceof Object) {
            Object.keys(elementtwo).forEach((elementthee) => {
              let val = '';
              if (elementtwo[elementthee] instanceof Array) {
                val = elementtwo[elementthee].join(',');
              } else {
                val = encodeURIComponent(elementtwo[elementthee]);
              }
              s.push({
                param: encodeURIComponent(element + '[' + iter + '].' + elementthee),
                value: val
              });
            });
          } else {
            s.push({
              param: encodeURIComponent(element),
              value: encodeURIComponent(elementtwo)
            });
          }
          iter++;
        });
      } else {
        s.push({
          param: encodeURIComponent(element),
          value: query[element]
        });
      }
    });
    return s;
  }

  SortToArray(multiSortMeta: SortMeta[]): any[] {
    const t: any[] = [];
    if (multiSortMeta == undefined) return [];
    multiSortMeta.forEach(element => {
      t.push({
        n: element.field,
        d: (element.order == 1) ? true : false,
      });
    });
    return t;
  }

  FilterToArray(filters: { [s: string]: FilterMetadata; }): any {
    const t: any[] = [];
    Object.keys(filters).forEach(element => {
      // Only add filters that have actual values (not null, undefined, or empty string)
      if (filters[element].value !== null &&
        filters[element].value !== undefined &&
        filters[element].value !== '') {
        t.push({
          n: element,
          v: filters[element].value,
          m: filters[element].matchMode
        });
      }
    });
    return t;
  }

  getValueForTable(set: Dataset[], val: any, type: string, col?: ColumnData) {
    if (val == null) return '';

    // Handle MultiSelectComplex specially
    if (type === 'multiselectcomplex' && col) {
      return this.getValueForMultiSelectComplex(col, val);
    }

    // Original logic for other types
    if (set != undefined) {
      const d = set.find(i => i.value == val);
      if (d != undefined && d.label != undefined) return `${val} - ${d.label}`;
    }
    if (type == 'bool') {
      const d = this.booleanDataSet.find(i => i.value == val);
      if (d !== undefined) return `${d.label}`;
    }
    if (type == 'date') { return moment(val).format('DD/MM/YYYY, h:mm:ss a'); }
    return val;
  }

  getValueForMultiSelectComplex(col: ColumnData, val: any): string {
    if (val == null || val == undefined) return '';

    // Try to find the label from columnset (which should have the labels)
    if (col.columnset && col.columnset.length > 0) {
      const item = col.columnset.find(i => i.value == val);
      if (item && item.label && item.label !== `${val}`) {
        return `${val} - ${item.label}`;
      }
    }

    // Fallback: just return the value
    return `${val}`;
  }

  /**
   * Gets selected rows
   */
  GetSelectedRows(): number[] {
    const selectedRows = [];
    this.selectedRows.forEach(element => {
      selectedRows.push(this.data.indexOf(element));
    });
    return selectedRows;
  }

  onSelectEdit() {

    this.lockColumnWidthsForEdit();
    if(this.isMultipleSelectionMode){
      this.GetSelectedRows().forEach(element => {
        const index = element;
        this.InitiateEditingForRow(index, true);
        this.editingObj.originalData[index] = { ...this.data[index] };
        this.selectedRows = [];
      });
    } else{
      const index = this.data.findIndex(e => e === this.selectedRows);
      this.InitiateEditingForRow(index, true);
      this.editingObj.originalData[index] = { ...this.data[index] };
    }


    setTimeout(() => {
      this.lockColumnWidthsForEdit();
    }, 0);
  }

  InitiateEditingForRow(index: number, boolVal: boolean) {
    // Set the editing value
    this.editingObj.isRowBeingEdited[index] = boolVal;

    // Check to see if the user is editing any rows
    let count = 0;
    this.editingObj.isRowBeingEdited.forEach(element => {
      count += (element) ? 1 : 0;
    });
    this.editingObj.isEditing = (count >= 1);
    this.editCount = count;


    this.cols.forEach((element: ColumnData) => {
      if (element.type == 'multiselectcomplex') {
        this.editForm.get(element.field + index + 'EditField').setValue(this.data[index][element.field]);
      } else {
        this.editForm.get(element.field + index + 'EditField').setValue(this.data[index][element.field]);
      }
    });

    this.ConfigureMultiSelectFieldParams('editRow', index + 'EditField');
  }

  editMultiselect(event) {
    const LIMIT_NUMBER = 1;
    if (event.value.length > LIMIT_NUMBER) { event.value.shift(); }
  }

  onSelectSave() {
    if(this.hasValidForm()) {

      const savePromises: Promise<any>[] = [];

      for (let i = 0; i < this.editingObj.isRowBeingEdited.length; i++) {
        if (this.editingObj.isRowBeingEdited[i]) {
          const getPrimaryString: string = this.GetPrimaryString(this.data[i]);
          const data = this.GetEditingData(i);

          const savePromise = this.apiHandler.putABCisRequest(this.configuration.apiConfig.api, this.formUrl + getPrimaryString, data).then(() => {
            this.SendMessage(true, 'success', 'Saved Successfully');
            this.InitiateEditingForRow(this.data.indexOf(this.data[i]), false);
          }).catch((error: HttpErrorResponse) => {
            console.log(error);
            this.SendMessage(true, 'error', error.error, 'Error');
          });
          savePromises.push(savePromise);
        }
      }
      // Wait for all save operations to complete
      Promise.all(savePromises).then(() => {
        this.RefreshTable();
      }).catch((error) => {
        console.error('Error during save operations', error);
      });
    } else {
      const formGroups = this.editForms.controls;

      for (let index = 0; index < formGroups.length; index++) {
        const fg = formGroups[index] as FormGroup;

        const errorMessage = this.getFormControlsErrorMessages(fg);

        this.SendMessage(true, 'error', errorMessage, 'Error');

      }
    }
  }

  hasValidForm() {
    const formArray = new FormArray([]);
    for (let i = 0; i < this.editingObj.isRowBeingEdited.length; i++) {
      if (this.editingObj.isRowBeingEdited[i]) {
        const group = {};
        this.cols.forEach(element => {
          let validators = [];
          if (element.addFormValidators != undefined) {
            validators = element.addFormValidators;
          }
          group[element.field] = new FormControl('', validators);
        });

        const fg = new FormGroup(group);
        fg.markAllAsTouched();
        fg.markAsDirty();
        fg.updateValueAndValidity();
        fg.patchValue(this.GetEditingData(i));

        formArray.push(fg);
      }
    }

    this.listEditForm.controls['editForms'] = formArray;

    return this.editForms.valid;
  }

  GetEditingData(index: number) {
    const ret: { [s: string]: any; } = {};
    this.cols.forEach((element: ColumnData) => {
      if (element.type == 'multiselectcomplex') {
        ret[element.field] = this.editForm.get(element.field + index + 'EditField').value;
      } else {
        ret[element.field] = this.editForm.get(element.field + index + 'EditField').value;
      }
    });
    return ret;
  }

  SendMessage(isEnabled: boolean, type: 'error' | 'success', message: string, title?: string) {
    if (!isEnabled) return;

    switch (type) {
    case 'error':
      this.logger.error(title || 'Error', message);
      break;
    case 'success':
      this.toastr.success(message, title || 'Success');
      break;
    }
  }

  GetPrimaryString(object: any): string {
    let ret = '';
    this.primaryFields.forEach(element => {
      ret += `/${object[element]}`;
    });
    return ret;
  }

  /**
   * Deletes the selected rows after confirmation
   */
  onSelectDelete() {
    this.displayIsLoading = true;
    let requests = [];

    if(this.isMultipleSelectionMode){
      requests = this.selectedRows.map(e => {
        const getPrimaryString: string = this.GetPrimaryString(e);
        return this.apiHandler.deleteABCisRequest(this.configuration.apiConfig.api, this.formUrl + getPrimaryString).then(() => this.InitiateEditingForRow(this.data.indexOf(e), false));
      });
    } else{
      requests.push(this.apiHandler.deleteABCisRequest(this.configuration.apiConfig.api, this.formUrl + this.GetPrimaryString(this.selectedRows)).then(() => this.InitiateEditingForRow(this.data.indexOf(this.selectedRows), false)));
    }

    forkJoin(requests).subscribe(() => {
      this.SendMessage(true, 'success', 'Deleted Successfully');
      this.displayDelete = false;
      this.data = this.data.filter(e => this.isMultipleSelectionMode ? !this.selectedRows.includes(e) : e !== this.selectedRows );
      this.displayIsLoading = false;
      this.selectedRows = [];
    }, err => {
      this.displayIsLoading = false;
      this.SendMessage(true, 'error', err.error, err.statusText);
    });
  }

  onSelectCancel() {
    this.selectedRows = [];
    for (let i = 0; i < this.editingObj.isRowBeingEdited.length; i++) {
      if (this.editingObj.isRowBeingEdited[i]) {
        const index = i;
        if (this.editingObj.isRowBeingEdited[index]) {
          this.InitiateEditingForRow(index, false);
          this.data[index] = this.editingObj.originalData[index];
          this.editingObj.originalData[index] = [];
        }
      }
    }
  }
  onSelectInput(event: any, field: string) {
    let val: any = null;

    try {
      if (event === null || event === undefined) {
        val = null;
      } else if (typeof event === 'boolean') {
        val = event;
      } else if (typeof event === 'number') {
        val = event;
      } else if (typeof event === 'string') {
        val = event;
      } else if (event instanceof Date) {
        val = event;
      } else if (event && event.target && event.target.value !== undefined) {
        // Handle checkbox specifically
        if (event.target.type === 'checkbox') {
          val = event.target.checked;
        } else {
          val = event.target.value;
        }
      } else if (event && event.value !== undefined) {
        val = event.value;
      } else {
        val = event;
      }
    } catch (error) {
      console.warn('Error processing input event:', error);
      val = null;
    }

    if (this.isEditingAll.value) {
      // Find the column configuration to determine the data type
      const columnConfig = this.cols.find(col => col.field === field);

      for (let i = 0; i < this.editingObj.isRowBeingEdited.length; i++) {
        if (this.editingObj.isRowBeingEdited[i]) {
          const control = this.editForm.get(field + i + 'EditField');
          if (control) {
            // Apply type-specific handling
            if (columnConfig && columnConfig.type === ColumnType.Boolean) {
              control.setValue(val === true || val === 'true'); // ← Ensure boolean
            } else if (columnConfig && columnConfig.type === ColumnType.Number) {
              control.setValue(Number(val)); // ← Ensure number
            } else if (columnConfig && columnConfig.type === ColumnType.Date) {
              control.setValue(val instanceof Date ? val : new Date(val)); // ← Ensure date
            } else {
              control.setValue(val); // ← Keep original value
            }
          }
        }
      }
    }
  }

  ConfigureAddForm() {
    this.addingForm.reset();
    this.cols.forEach((element: ColumnData) => {
      const control = this.addingForm.get(element.field);
      if (control) {
        const defaultValue = element.addFormDefault !== undefined && element.addFormDefault !== null
          ? element.addFormDefault
          : null;
        control.setValue(defaultValue);

        control.markAsUntouched();
        control.markAsPristine();
      }
    });

    this.cdr.detectChanges();
    this.ConfigureMultiSelectFieldParams('addRow', '');
  }

  /**
   * The on submit event for the add row form.
   * Simply sends a post to add a row to the table.
   * @param form
   */
  onSelectAdd() {
    const errorMessage = this.getFormControlsErrorMessages(this.addingForm);
    if(errorMessage === ''){
      this.displayIsLoading = true;
      this.apiHandler.postABCisRequest(this.configuration.apiConfig.api, this.formUrl, this.addingForm.value).then(() => {
        this.displayAdd = false;
        this.displayIsLoading = false;
        this.resetAddForm(); // Reset form after successful add
        this.SendMessage(true, 'success', 'Added Successfully');
        this.InitializeFieldProperties();
        this.RefreshTable();
      }).catch((response: HttpErrorResponse) => {
        this.displayIsLoading = false;
        console.log(response);
        this.SendMessage(true, 'error', response.error, 'Error');
      });
    } else {
      this.SendMessage(true, 'error', errorMessage, 'Error');
    }
  }

  /**
   * Validate Formcontrols
   */
  private getFormControlsErrorMessages(fg: FormGroup): string {
    const formControls = fg.controls;
    let errorMessage = '';
    const formControlKeys = Object.keys(formControls);
    let field = '';
    let errorField = '';
    debugger;
    for (let controlIndex = 0; controlIndex < formControlKeys.length; controlIndex++) {
      field = formControlKeys[controlIndex];
      const controlErrors = fg.get(field).errors;

      if(controlErrors !== null) {
        const controlErrorsKeys = Object.keys(controlErrors);
        for (let errorIndex = 0; errorIndex < controlErrorsKeys.length; errorIndex++) {
          errorField = controlErrorsKeys[errorIndex];
          if(errorField === 'required') {
            errorMessage = `${this.cols.find(e => e.field === field).addFormErrorMessages.find(e => e.key.toLowerCase() === errorField).message}`;
          } else {
            errorMessage = `Invalid ${field.toUpperCase()}: ${fg.get(field).value}. ${this.cols.find(e => e.field === field).addFormErrorMessages.find(e => e.key.toLowerCase() === errorField).message}`;
          }

          break;
        }
      }

      if(errorMessage !== '') {
        break;
      }
    }

    return errorMessage;
  }

  onAddDialogHide() {
    this.resetAddForm();
  }

  /**
   * Reset add form completely
   */
  private resetAddForm() {
    this.addingForm.reset();

    this.cols.forEach((element: ColumnData) => {
      const control = this.addingForm.get(element.field);
      if (control) {
        control.setValue(null);
        control.markAsUntouched();
        control.markAsPristine();
        control.setErrors(null);
      }
    });

    this.cols.forEach((element: ColumnData) => {
      if (element.type === ColumnType.MultiSelectComplex) {
        if (element.addedComplexFieldsUrlParam === undefined) {
          element.addedComplexFieldsUrlParam = {};
        }
        element.addedComplexFieldsUrlParam['addRow'] = '';
      }
    });

    this.cdr.detectChanges();
  }

  /**
   * Show add row dialog
   */
  displayAdd = false;
  displayIsLoading = false;
  showAddRowDialog() {
    if (!this.configuration.hasCustomAdd) {
      this.resetAddForm(); // Reset before showing
      this.displayAdd = true;
      this.ConfigureAddForm();
    } else {
      this.customAdd.emit();
    }
  }

  /**
   *
   * Show edit text area dialog
   */
  showTextAreaModal = false;
  textAreaModalValue = '';
  editingTextAreaField = '';
  editingRowIndex: number;
  openTextAreaModal(ri: number, col: any) {
    this.editingTextAreaField = col.field + ri + 'EditField';
    this.textAreaModalValue = this.editForm.get(this.editingTextAreaField)?.value || '';
    this.editingRowIndex = ri;
    this.showTextAreaModal = true;
  }

  applyTextAreaModal() {
    if (this.editingTextAreaField) {
      this.editForm.get(this.editingTextAreaField)?.setValue(this.textAreaModalValue);
    }
    this.showTextAreaModal = false;
  }

  onTextAreaModalHide() {
    this.textAreaModalValue = '';
    this.editingTextAreaField = '';
  }

  /**
   * Show delete row dialog
   */
  displayDelete = false;
  showDeleteRowDialog() {
    if (this.isMultipleSelectionMode ? this.GetSelectedRows().length > 0 : true) {
      this.displayDelete = true;
    } else {
      this.toastr.warning('No Rows Selected');
    }
  }

  /**
   *  Show advanced text filtering dialog
   * @param column The column being advanced searched
   */
  displayAdvancedFilter = false;
  filteringField: string;
  andvancedFilterInput = new FormControl('');
  advancedFilterType = new FormControl('');
  showAdvancedFilterDialog(column) {
    this.filteringField = column.field;
    const colNum = this.findWithAttr(this.cols, 'field', this.filteringField);
    this.andvancedFilterInput.setValue(this.cols[colNum].value);
    this.advancedFilterType.setValue(this.cols[colNum].matchmode);
    this.displayAdvancedFilter = true;
  }

  onAdvancedFilter() {
    setTimeout(() => {
      this.dataTable.filter(this.andvancedFilterInput.value, this.filteringField, this.advancedFilterType.value);
      this.displayAdvancedFilter = false;
      const colNum = this.findWithAttr(this.cols, 'field', this.filteringField);
      this.cols[colNum].matchmode = this.advancedFilterType.value;
      this.cols[colNum].value = this.andvancedFilterInput.value;
    }, 250);
  }

  findWithAttr(array, attr, value) {
    for (let i = 0; i < array.length; i += 1) {
      if (array[i][attr] === value) {
        return i;
      }
    }
    return -1;
  }

  linkOnClick(col, value) {
    this.linkClick.emit({ column: col.field, value: value });
  }

  actionOnClick(col: ColumnData, row: any) {
    this.actionClick.emit({ column: col.field, row: row, action: col.actionLabel || col.field });
  }

  getColumnWidth(col: ColumnData): string {

    // If width is manually set, use it directly - don't override it
    if (col.width) {
      return col.width;
    }
    // Otherwise use calculated width or fallback
    return col.calculatedWidth || 'auto';
  }


  clearAllFilters() {
    try {
      if (this.searchForm) {
        this.searchForm.reset();
      }

      if (this.cols) {
        this.cols.forEach((col: ColumnData) => {
          if (col.matchmode == undefined) col.matchmode = MatchMode.Contains;
          if (col.type == ColumnType.MultiSelect) col.matchmode = MatchMode.In;
          if (col.type == ColumnType.MultiSelectComplex) col.matchmode = MatchMode.In;

          const searchControl = this.searchForm?.get(col.field + 'SearchField');
          if (searchControl) {
            searchControl.setValue(null);

            // Clear the typed input for multiselect components
            if (col.type === ColumnType.MultiSelectComplex) {
              // Trigger a manual clear on the search control
              searchControl.markAsPristine();
              searchControl.markAsUntouched();
            }
          }
        });
      }

      if (this.dataTable) {
        this.dataTable.filters = {};
        this.dataTable.sortField = null;
        this.dataTable.sortOrder = 0;
        this.dataTable.multiSortMeta = [];
        this.dataTable._sortField = null;
        this.dataTable._sortOrder = 0;
        this.dataTable._multiSortMeta = [];

        // Reset pagination to first page
        this.dataTable.first = 0;
        this.totalRecords = this.configuration?.apiConfig?.approxamateRowCount || 0;

        // Clear the table and reload
        this.dataTable.reset();
      }

      this.resetMultiselectFilters();
      this.defaultConfiguration.MultiSortMeta = [];

      setTimeout(() => {
        this.cdr.detectChanges();
      }, 100);
    } catch (error) {
      console.error('Error clearing filters and sorting:', error);
    }
  }

  onMultiselectChange(values: any, col: any) {
    let filterValue = values;
    let matchMode = col.matchmode;

    // Handle different scenarios
    if (values === null || values === undefined || (Array.isArray(values) && values.length === 0)) {
      // Clear the filter
      filterValue = null;
    } else if (col.isFilterSingleSelect) {
      // For single select filter, always use equals match mode
      filterValue = Array.isArray(values) ? values[0] : values;
      matchMode = 'e'; // Equals
    } else if (Array.isArray(values)) {
      if (values.length === 1) {
        // Single value - use Equals match mode
        filterValue = values[0];
        matchMode = 'e'; // Equals
      } else {
        // Multiple values - use In match mode and join with commas
        filterValue = values.join(',');
        matchMode = 'i'; // In
      }
    } else {
      // Single value (not array) - use Equals match mode
      filterValue = values;
      matchMode = 'e'; // Equals
    }

    setTimeout(() => {
      // this.dataTable.first = 0;
      this.dataTable.filter(filterValue, col.field, matchMode);
    }, 0);
  }

  public resetMultiselectFilters(): void {
    this.cols.forEach((col: ColumnData) => {
      if (col.type === ColumnType.MultiSelectComplex) {
        const searchControl = this.searchForm.get(col.field + 'SearchField');
        if (searchControl) {
          searchControl.setValue(null);
          // Force the control to emit the change
          searchControl.updateValueAndValidity();
        }
      }
    });
  }

  private forceDateColumnWidths() {
    setTimeout(() => {
      const table = this.dataTable.el.nativeElement;
      const dateColumns = table.querySelectorAll('th.date-column, td.date-column');

      dateColumns.forEach((element: HTMLElement) => {
        element.style.width = '110px';
        element.style.minWidth = '110px';
        element.style.maxWidth = '110px';
        element.style.flex = '0 0 110px';

        // Force all children to 100% width
        element.querySelectorAll('*').forEach((child: HTMLElement) => {
          child.style.width = '100%';
          child.style.maxWidth = '100%';
          child.style.boxSizing = 'border-box';
        });
      });
    }, 100);
  }

  private forceBooleanColumnWidths() {
    setTimeout(() => {
      const table = this.dataTable.el.nativeElement;
      const booleanHeaders = table.querySelectorAll('th.boolean-column');
      const booleanCells = table.querySelectorAll('td.boolean-column');

      // Force header widths
      booleanHeaders.forEach((header: HTMLElement) => {
        header.style.width = '100px';
        header.style.minWidth = '100px';
        header.style.maxWidth = '100px';
        header.style.flex = '0 0 100px';
      });

      // Force cell widths
      booleanCells.forEach((cell: HTMLElement) => {
        cell.style.width = '100px';
        cell.style.minWidth = '100px';
        cell.style.maxWidth = '100px';
        cell.style.flex = '0 0 100px';
      });
    }, 0);
  }

  /* Calendar - Dates */
  filterDate(event: any, shouldReverse: boolean, field: string) {
    if (!event || !field) {
      this.clearDateFilter(field);
      return;
    }
    setTimeout(() => {
      try {
        let filterValue: string;
        if (shouldReverse && typeof event === 'string') {
          filterValue = event.split('/').reverse().join('-');
        } else if (event instanceof Date) {
          filterValue = moment(event).format('YYYY-MM-DD');
        } else {
          filterValue = moment(event).format('YYYY-MM-DD');
        }

        if (this.dataTable) {
          // this.dataTable.first = 0;
          this.dataTable.filter(filterValue, field, 'contains');
        }
      } catch (error) {
        this.clearDateFilter(field);
      }
    }, 50);
  }

  onDateInputFocus(event: any) {
    event.target.parentElement.classList.add('p-inputwrapper-focus');
  }

  onDateInputBlur(event: any) {
    event.target.parentElement.classList.remove('p-inputwrapper-focus');
  }

  formatDateForDisplay(dateValue: string): string {
    if (!dateValue) return '';

    try {
      const date = new Date(dateValue);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateValue;
    }
  }

  clearDateFilter(field: string) {
    const searchControl = this.searchForm.get(field + 'SearchField');
    if (searchControl) {
      searchControl.setValue(null);
    }
    if (this.dataTable) {
      this.dataTable.filter('', field, 'contains');
    }
  }

  private lockColumnWidthsForEdit() {
    setTimeout(() => {
      const table = this.dataTable?.el?.nativeElement;
      if (!table) return;

      const tableElement = table.querySelector('table');
      if (tableElement) {
        // Ensure fixed layout is maintained
        tableElement.style.tableLayout = 'fixed';
        tableElement.style.width = '100%';
      }

      // Don't recalculate widths, just ensure inputs fit within existing cells
      const allInputs = table.querySelectorAll('input, .p-dropdown, .p-calendar, .p-multiselect, primeng-multiselect, textarea');
      allInputs.forEach((input: HTMLElement) => {
        input.style.width = '100%';
        input.style.maxWidth = '100%';
        input.style.boxSizing = 'border-box';
      });
    }, 10);
  }
}

class Query {
  sf: any | string[]; // Select
  f: any[] | { [s: string]: FilterMetadata; }; // Where
  ob: any[]; // Order By
  fr: number; // Limit first
  rc: number; // Limit count
}

class EditObject {
  isEditing = false;
  originalData: any[] = [];
  isRowBeingEdited: boolean[] = [];
  addedRowValue: { [s: string]: string }[] = [];
}

export enum ColumnType {
  String = 'string',
  TextArea = 'textarea',
  Date = 'date',
  DropDown = 'dropdown',
  MultiSelectComplex = 'multiselectcomplex',
  MultiSelect = 'multiselect',
  Boolean = 'bool',
  Number = 'number',
  Action = 'action'
}

export enum ColumnValues {
  Alphanumeric = 'alphanumeric',
  Number = 'number'
}

export enum MatchMode {
  Contains = 'c',
  StartsWith = 's',
  Equals = 'e',
  In = 'i'
}

export enum LinkType {
  Modal, Redirect
}

export class ABCisTableModel {
  queryParameterName?: string;
  apiConfig: ABCisApiConfig;
  addFormConfig?: ABCisAddFormConfig;
  colConfig: any[] | ColumnData[];
  isTable?: boolean = true;
  isConfigurable = true;
  isFilterable?: boolean = true;
  canAdd?: boolean = true;
  hasCustomAdd?: boolean = false;
  canEdit?: boolean = true;
  canDelete?: boolean = true;
  selectionMode?: string = 'multiple';
  metaKeySelection?: boolean = true;
  initialRowCount?: number;
}

export class ABCisAddFormConfig {
  header?: string;
}

export class ABCisApiConfig {
  api: ABCisApi;
  url: string;
  table: string;
  urlParams?: { name: string, value: string }[] = [];
  approxamateRowCount: number;
  anyParams?: boolean = true;
  queryParameters?: { name: string, value: string, mode: string }[] = [];
}

export class ColumnData {
  field: string;
  header: string;
  type: ColumnType = ColumnType.String;
  matchmode?: MatchMode = MatchMode.Contains;
  isLink?: boolean = false;
  limit?: number = 5000;
  accept?: string = 'alphanumeric';

  width?: string; // e.g., "100px", "15%"
  minWidth?: string; // e.g., "60px"
  calculatedWidth?: string; // Calculated by the component

  setUrl?: string;
  dataset?: Dataset[];
  columnset?: Dataset[];
  allowedInsertDataset?: Dataset[];
  isReadonly?: boolean = false;
  // Readonly only while editing existing rows (still editable on add form)
  isReadonlyOnEdit?: boolean = false;
  isPrimary?: boolean;
  notInForm?: boolean = false;
  isHidden?:boolean =false;
  addFormDefault?: number | string | boolean;
  addFormValidators: ValidatorFn[] = [];
  addFormErrorMessages?: { key: string, message: string }[] = [];
  sortable?: boolean = true;
  actionLabel?: string;
  actionIcon?: string;
  actionClass?: string;
  actionTooltip?: string;

  addedComplexFields?: string[];
  addedComplexFieldsUrlParam?: { [s: string]: string; } = {};

  // Controls single/multi selection for add/edit
  isSingleSelect?: boolean = true;

  // Controls single/multi selection for filtering
  isFilterSingleSelect?: boolean = false;

  // Controls whether filter calls backend or uses existing data
  isFilterBackendSearch?: boolean = true; // true = call backend, false = use existing data only
}

export class Dataset {
  label: string;
  value: any;
}

